import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

interface CancelSubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  endDate: string | null;
  isLoading: boolean;
}

const CancelSubscriptionDialog: React.FC<CancelSubscriptionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  endDate,
  isLoading,
}) => {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'the end of your billing period';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'the end of your billing period';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Cancel Subscription
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your subscription will remain active until {formatDate(endDate)}
        </Alert>
        <Typography variant="body1" paragraph>
          Are you sure you want to cancel your subscription? After{' '}
          <strong>{formatDate(endDate)}</strong>, you will lose access to:
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 2 }}>
          <li>
            <Typography variant="body2">All paid features and AI capabilities</Typography>
          </li>
          <li>
            <Typography variant="body2">Access to your clinical data and records</Typography>
          </li>
          <li>
            <Typography variant="body2">Veterinarian accounts</Typography>
          </li>
        </Box>
        <Typography variant="body2" color="text.secondary">
          You can resubscribe at any time to regain full access to your account.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={isLoading}>
          Keep Subscription
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isLoading ? 'Canceling...' : 'Yes, Cancel Subscription'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CancelSubscriptionDialog;

